import React from 'react'
import {
  Box,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import dayjs from 'dayjs'
import { EnvelopeIcon, NoSymbolIcon } from '@heroicons/react/24/outline'
import type { OrgInvitation } from 'pure-js/types/OrgInvitation'
import { OrgInvitationStatus } from 'pure-js/types/OrgInvitation'

import { Spinner } from '../../../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { OrgRoleChip } from '../../components/OrgRoleChip'
import { OrgInviteStatusChip } from '../../components/OrgInviteStatusChip'
import {
  orgTableBodyCellSx,
  orgTableCardSx,
  orgTableHeadCellSx,
  orgTablePaginationSx,
  orgTableRowSx
} from '../../components/orgTableStyles'

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50]

interface OrgInvitesTableProps {
  rows: OrgInvitation[]
  totalFiltered: number
  isOrgAdmin: boolean
  /** Current user id — facilitators may revoke pending invites they created themselves. */
  currentUserId?: string
  onRevoke: (invite: OrgInvitation) => void
  isLoading: boolean
  isError: boolean
  hasNoInvites: boolean
  hasNoMatches: boolean
  page: number
  rowsPerPage: number
  onPageChange: (page: number) => void
  onRowsPerPageChange: (rowsPerPage: number) => void
}

export const OrgInvitesTable: React.FC<OrgInvitesTableProps> = ({
  rows,
  totalFiltered,
  isOrgAdmin,
  currentUserId,
  onRevoke,
  isLoading,
  isError,
  hasNoInvites,
  hasNoMatches,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange
}) => {
  const { t } = useTranslation()
  const showPagination = !isLoading && !isError && totalFiltered > rowsPerPage

  return (
    <Box sx={orgTableCardSx}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={orgTableHeadCellSx}>{t('org.invites.columns.email')}</TableCell>
              <TableCell sx={orgTableHeadCellSx}>{t('org.invites.columns.role')}</TableCell>
              <TableCell sx={orgTableHeadCellSx}>{t('org.invites.columns.status')}</TableCell>
              <TableCell sx={orgTableHeadCellSx}>{t('org.invites.columns.expires')}</TableCell>
              <TableCell sx={{ ...orgTableHeadCellSx, textAlign: 'right' }}>
                {t('org.invites.columns.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} sx={{ ...orgTableBodyCellSx, py: 6, textAlign: 'center', border: 'none' }}>
                  <Spinner />
                </TableCell>
              </TableRow>
            )}

            {isError && (
              <TableRow>
                <TableCell
                  colSpan={5}
                  sx={{
                    ...orgTableBodyCellSx,
                    py: 6,
                    textAlign: 'center',
                    color: INTERAX_COLORS.redError,
                    border: 'none'
                  }}
                >
                  {t('common.error')}
                </TableCell>
              </TableRow>
            )}

            {hasNoInvites && (
              <TableRow>
                <TableCell colSpan={5} sx={{ ...orgTableBodyCellSx, py: 6, textAlign: 'center', border: 'none' }}>
                  <Stack alignItems="center" spacing={1.5}>
                    <EnvelopeIcon style={{ width: 40, height: 40, color: INTERAX_COLORS.white40 }} />
                    <Typography sx={{ color: INTERAX_COLORS.white70 }}>{t('org.invites.emptyAll')}</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}

            {hasNoMatches && (
              <TableRow>
                <TableCell colSpan={5} sx={{ ...orgTableBodyCellSx, py: 6, textAlign: 'center', border: 'none' }}>
                  <Stack alignItems="center" spacing={1.5}>
                    <EnvelopeIcon style={{ width: 40, height: 40, color: INTERAX_COLORS.white40 }} />
                    <Typography sx={{ color: INTERAX_COLORS.white70 }}>{t('org.invites.empty')}</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              !isError &&
              rows.map((inv) => (
                <TableRow key={inv.id} sx={orgTableRowSx} data-testid={`org-invites-row-${inv.id}`}>
                  <TableCell sx={{ ...orgTableBodyCellSx, wordBreak: 'break-all' }}>{inv.email}</TableCell>
                  <TableCell sx={orgTableBodyCellSx}>
                    <OrgRoleChip role={inv.role} size="small" />
                  </TableCell>
                  <TableCell sx={orgTableBodyCellSx}>
                    <OrgInviteStatusChip status={inv.status} />
                  </TableCell>
                  <TableCell sx={{ ...orgTableBodyCellSx, color: INTERAX_COLORS.white70 }}>
                    {inv.expiresAt ? dayjs(inv.expiresAt).format('DD MMM YYYY') : '—'}
                  </TableCell>
                  <TableCell sx={{ ...orgTableBodyCellSx, textAlign: 'right' }}>
                    {inv.status === OrgInvitationStatus.PENDING &&
                      (isOrgAdmin || inv.invitedByUserId === currentUserId) && (
                        <IconButton
                          size="small"
                          onClick={() => onRevoke(inv)}
                          aria-label={t('org.invites.revoke')}
                          data-testid="org-invites-row-revoke"
                          sx={{
                            color: INTERAX_COLORS.redError,
                            '&:hover': { backgroundColor: 'rgba(248, 113, 113, 0.12)' }
                          }}
                        >
                          <NoSymbolIcon style={{ width: 16, height: 16 }} />
                        </IconButton>
                      )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && (
        <TablePagination
          component="div"
          count={totalFiltered}
          page={page}
          onPageChange={(_, p) => onPageChange(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          labelRowsPerPage={t('org.members.rowsPerPage') as string}
          sx={orgTablePaginationSx}
        />
      )}
    </Box>
  )
}
